'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { CapabilityMatch, ProjectRequirements } from './ExploreSolutionsClient';

interface MatchResultsPanelProps {
  matches: CapabilityMatch[];
  hasSearched: boolean;
  isAnalyzing: boolean;
  requirements: ProjectRequirements;
}

const getScoreConfig = (score: number) => {
  if (score >= 70) return {
    bar: '#1E7B3C', text: 'text-aerospace-green', border: 'rgba(30,123,60,0.4)',
    bg: 'rgba(30,123,60,0.08)', glow: 'rgba(30,123,60,0.3)', label: 'STRONG MATCH', ring: '#1E7B3C',
  };
  if (score >= 40) return {
    bar: '#00D4FF', text: 'text-accent', border: 'rgba(0,212,255,0.4)',
    bg: 'rgba(0,212,255,0.08)', glow: 'rgba(0,212,255,0.3)', label: 'PARTIAL MATCH', ring: '#00D4FF',
  };
  return {
    bar: '#4B5563', text: 'text-technical-gray', border: 'rgba(255,255,255,0.1)',
    bg: 'rgba(255,255,255,0.03)', glow: 'none', label: 'LOW MATCH', ring: '#374151',
  };
};

const CAPACITY_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
  high: { label: 'AVAILABLE', color: 'text-aerospace-green', dot: '#1E7B3C' },
  medium: { label: 'LIMITED', color: 'text-warning', dot: '#F59E0B' },
  low: { label: 'CONSTRAINED', color: 'text-error', dot: '#EF4444' },
};

const AnimatedScoreRing = ({ score, color, size = 64 }: { score: number; color: string; size?: number }) => {
  const [animScore, setAnimScore] = useState(0);
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDash = (animScore / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => {
      let current = 0;
      const step = score / 40;
      const interval = setInterval(() => {
        current = Math.min(current + step, score);
        setAnimScore(Math.round(current));
        if (current >= score) clearInterval(interval);
      }, 25);
      return () => clearInterval(interval);
    }, 300);
    return () => clearTimeout(timer);
  }, [score]);

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={`${strokeDash} ${circumference}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.05s linear', filter: `drop-shadow(0 0 4px ${color})` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-accent text-sm leading-none" style={{ color }}>{animScore}</span>
        <span className="font-cta text-[8px] text-technical-gray/60 tracking-wider mt-0.5">%</span>
      </div>
    </div>
  );
};

const MatchResultsPanel = ({ matches, hasSearched, isAnalyzing, requirements }: MatchResultsPanelProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [animatedBars, setAnimatedBars] = useState(false);

  useEffect(() => {
    if (hasSearched) {
      const t = setTimeout(() => setAnimatedBars(true), 200);
      return () => clearTimeout(t);
    }
    setAnimatedBars(false);
  }, [hasSearched]);

  const toggleCompare = (id: string) => {
    setCompareIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  };

  const compareItems = matches.filter((m) => compareIds.includes(m.id));

  if (isAnalyzing) {
    return (
      <div className="rounded-2xl overflow-hidden min-h-[500px] flex flex-col items-center justify-center gap-8 relative"
        style={{ background: 'linear-gradient(180deg, #1A2332 0%, #141C28 100%)', border: '1px solid rgba(0,212,255,0.2)' }}>
        {/* Animated radar */}
        <div className="relative w-40 h-40">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="absolute inset-0 rounded-full border border-accent/20 animate-ping"
              style={{ animationDelay: `${i * 0.3}s`, animationDuration: '2s' }} />
          ))}
          <div className="absolute inset-0 rounded-full border border-accent/10" />
          <div className="absolute inset-4 rounded-full border border-accent/20" />
          <div className="absolute inset-8 rounded-full border border-accent/30" />
          {/* Rotating sweep */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <div className="absolute inset-0 rounded-full"
              style={{
                background: 'conic-gradient(from 0deg, transparent 0deg, rgba(0,212,255,0.15) 60deg, transparent 60deg)',
                animation: 'spin 2s linear infinite',
              }} />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon name="CpuChipIcon" size={28} className="text-accent" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <p className="font-cta text-accent text-sm tracking-[0.2em]">SCANNING CAPABILITIES</p>
          <p className="font-body text-technical-gray text-sm">Cross-referencing requirements against manufacturing stack...</p>
        </div>

        {/* Animated data lines */}
        <div className="w-72 space-y-2">
          {['Analyzing materials...', 'Matching processes...', 'Checking certifications...', 'Computing relevance scores...'].map((line, i) => (
            <div key={line} className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
              <div className="flex-1 h-px bg-white/5 overflow-hidden rounded-full">
                <div className="h-full bg-accent/60 rounded-full"
                  style={{ animation: `scanBar 1.5s ease-in-out ${i * 0.3}s infinite alternate` }} />
              </div>
              <span className="font-cta text-[9px] text-technical-gray/60 tracking-wider w-24 text-right">{line}</span>
            </div>
          ))}
        </div>

        <style jsx>{`
          @keyframes scanBar {
            from { width: 20%; }
            to { width: 90%; }
          }
        `}</style>
      </div>
    );
  }

  if (!hasSearched) {
    return (
      <div className="rounded-2xl overflow-hidden min-h-[500px] flex flex-col items-center justify-center gap-6 relative"
        style={{ background: 'linear-gradient(180deg, #1A2332 0%, #141C28 100%)', border: '1px solid rgba(255,255,255,0.07)' }}>
        {/* Background grid decoration */}
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(0,212,255,0.1) 0%, transparent 60%)',
          }} />

        <div className="relative text-center space-y-4 max-w-sm px-6">
          {/* Icon cluster */}
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 rounded-2xl border border-accent/10 rotate-12" />
            <div className="absolute inset-0 rounded-2xl border border-accent/10 -rotate-6" />
            <div className="absolute inset-0 rounded-2xl flex items-center justify-center"
              style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
              <Icon name="ChartBarIcon" size={36} className="text-accent/40" />
            </div>
          </div>

          <div>
            <p className="font-cta text-sm text-accent/60 tracking-[0.2em] mb-2">AWAITING INPUT</p>
            <p className="font-body text-technical-gray text-sm leading-relaxed">
              Define your project parameters on the left panel and click <span className="text-accent font-cta text-xs">MATCH CAPABILITIES</span> to see precision-ranked results.
            </p>
          </div>

          {/* Parameter indicators */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            {[
              { label: 'Materials', icon: 'CubeIcon' },
              { label: 'Processes', icon: 'CogIcon' },
              { label: 'Certifications', icon: 'ShieldCheckIcon' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-2 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Icon name={item.icon as any} size={16} className="text-technical-gray/40" />
                <span className="font-cta text-[9px] text-technical-gray/50 tracking-wider">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const topMatch = matches[0];
  const relevantMatches = matches.filter((m) => m.score > 0);
  const noMatchCount = matches.filter((m) => m.score === 0).length;

  return (
    <div className="space-y-4">
      {/* ── SUMMARY BAR ── */}
      <div className="rounded-xl overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1A2332 0%, #141C28 100%)', border: '1px solid rgba(30,123,60,0.3)', boxShadow: '0 0 20px rgba(30,123,60,0.1)' }}>
        <div className="px-5 py-4 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(30,123,60,0.15)', border: '1px solid rgba(30,123,60,0.4)' }}>
              <Icon name="CheckCircleIcon" size={18} className="text-aerospace-green" />
            </div>
            <div>
              <p className="font-cta text-xs text-aerospace-green tracking-[0.15em]">ANALYSIS COMPLETE</p>
              <p className="font-body text-xs text-technical-gray mt-0.5">
                {relevantMatches.length} of {matches.length} capabilities matched your requirements
              </p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-center">
              <p className="font-accent text-2xl leading-none" style={{ color: '#00D4FF' }}>{topMatch?.score ?? 0}%</p>
              <p className="font-cta text-[9px] text-technical-gray tracking-wider mt-1">TOP SCORE</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <p className="font-accent text-2xl leading-none text-foreground">{relevantMatches.length}</p>
              <p className="font-cta text-[9px] text-technical-gray tracking-wider mt-1">MATCHES</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <p className="font-accent text-2xl leading-none text-foreground">{noMatchCount}</p>
              <p className="font-cta text-[9px] text-technical-gray tracking-wider mt-1">NO MATCH</p>
            </div>
            {compareIds.length > 0 && (
              <button onClick={() => setCompareIds([])}
                className="px-3 py-1.5 rounded-lg font-cta text-[10px] tracking-wider transition-colors"
                style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', color: '#00D4FF' }}>
                CLEAR ({compareIds.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── COMPARE TABLE ── */}
      {compareIds.length >= 2 && (
        <div className="rounded-xl overflow-hidden"
          style={{ background: 'linear-gradient(180deg, #1A2332 0%, #141C28 100%)', border: '1px solid rgba(0,212,255,0.2)' }}>
          <div className="px-5 py-3 border-b flex items-center gap-2"
            style={{ borderColor: 'rgba(0,212,255,0.15)', background: 'rgba(0,212,255,0.05)' }}>
            <Icon name="ArrowsRightLeftIcon" size={14} className="text-accent" />
            <span className="font-cta text-xs text-accent tracking-[0.15em]">SIDE-BY-SIDE COMPARISON</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <th className="text-left px-4 py-3 font-cta text-technical-gray/60 tracking-wider w-28">ATTRIBUTE</th>
                  {compareItems.map((item) => {
                    const cfg = getScoreConfig(item.score);
                    return (
                      <th key={item.id} className="text-left px-4 py-3 font-cta tracking-wider" style={{ color: cfg.ring }}>
                        {item.title}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    label: 'SCORE', render: (item: CapabilityMatch) => {
                      const cfg = getScoreConfig(item.score);
                      return <span className={`font-accent text-xl ${cfg.text}`}>{item.score}%</span>;
                    }
                  },
                  {
                    label: 'CAPACITY', render: (item: CapabilityMatch) => {
                      const cap = CAPACITY_CONFIG[item.capacityStatus];
                      return <span className={`font-cta text-xs ${cap.color}`}>● {cap.label}</span>;
                    }
                  },
                  {
                    label: 'CERTS', render: (item: CapabilityMatch) =>
                      <span className="font-body text-foreground">{item.certifications.join(', ')}</span>
                  },
                ].map((row, ri) => (
                  <tr key={row.label} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td className="px-4 py-2.5 font-cta text-[10px] text-technical-gray/60 tracking-wider">{row.label}</td>
                    {compareItems.map((item) => (
                      <td key={item.id} className="px-4 py-2.5">{row.render(item)}</td>
                    ))}
                  </tr>
                ))}
                {compareItems[0]?.specs.map((spec, i) => (
                  <tr key={spec.label} style={{ borderBottom: i < compareItems[0].specs.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                    <td className="px-4 py-2.5 font-cta text-[10px] text-technical-gray/60 tracking-wider">{spec.label.toUpperCase()}</td>
                    {compareItems.map((item) => (
                      <td key={item.id} className="px-4 py-2.5 font-body text-foreground">{item.specs[i]?.value ?? '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── MATCH CARDS ── */}
      <div className="space-y-3">
        {matches.map((cap, index) => {
          const cfg = getScoreConfig(cap.score);
          const isExpanded = expandedId === cap.id;
          const isCompared = compareIds.includes(cap.id);
          const isTop = index === 0 && cap.score > 0;
          const capConfig = CAPACITY_CONFIG[cap.capacityStatus];

          return (
            <div key={cap.id}
              className="rounded-2xl overflow-hidden transition-all duration-300"
              style={{
                background: 'linear-gradient(180deg, #1A2332 0%, #141C28 100%)',
                border: `1px solid ${isTop ? 'rgba(30,123,60,0.5)' : cfg.border}`,
                boxShadow: isTop ? '0 0 30px rgba(30,123,60,0.15)' : cap.score > 0 ? `0 0 20px ${cfg.glow}` : 'none',
                opacity: cap.score === 0 ? 0.45 : 1,
              }}>

              {/* Best match banner */}
              {isTop && (
                <div className="px-5 py-2 flex items-center gap-2"
                  style={{ background: 'linear-gradient(90deg, rgba(30,123,60,0.2) 0%, transparent 100%)', borderBottom: '1px solid rgba(30,123,60,0.2)' }}>
                  <Icon name="StarIcon" size={12} className="text-aerospace-green" />
                  <span className="font-cta text-[10px] text-aerospace-green tracking-[0.2em]">BEST MATCH — HIGHEST RELEVANCE SCORE</span>
                  <div className="ml-auto flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-aerospace-green animate-pulse" />
                    <span className="font-cta text-[9px] text-aerospace-green/70 tracking-wider">RECOMMENDED</span>
                  </div>
                </div>
              )}

              <div className="px-5 py-4">
                <div className="flex items-start gap-4">
                  {/* Animated score ring */}
                  <AnimatedScoreRing score={cap.score} color={cfg.ring} size={68} />

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Icon name={cap.icon as any} size={14} className={cfg.text} />
                          <h3 className="font-body text-sm font-bold text-foreground">{cap.title}</h3>
                          <span className="px-2 py-0.5 rounded-full font-cta text-[9px] tracking-wider"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#8B9AAB' }}>
                            {cap.category}
                          </span>
                          <span className={`font-cta text-[9px] tracking-wider ${cfg.text}`}>{cfg.label}</span>
                        </div>
                        <p className="font-body text-xs text-technical-gray leading-relaxed">{cap.description}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: capConfig.dot }} />
                        <span className={`font-cta text-[10px] tracking-wider ${capConfig.color}`}>{capConfig.label}</span>
                      </div>
                    </div>

                    {/* Score bar */}
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <div className="h-full rounded-full transition-all duration-1000"
                          style={{
                            width: animatedBars ? `${cap.score}%` : '0%',
                            background: `linear-gradient(90deg, ${cfg.ring}, ${cfg.ring}99)`,
                            boxShadow: cap.score > 0 ? `0 0 8px ${cfg.glow}` : 'none',
                          }} />
                      </div>
                      <span className={`font-cta text-xs ${cfg.text} w-8 text-right`}>{cap.score}%</span>
                    </div>

                    {/* Matched factors */}
                    {cap.matchedFactors.length > 0 && (
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {cap.matchedFactors.slice(0, 5).map((f) => (
                          <span key={f} className="px-2 py-0.5 rounded-full font-cta text-[9px] tracking-wider"
                            style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.ring }}>
                            ✓ {f}
                          </span>
                        ))}
                        {cap.matchedFactors.length > 5 && (
                          <span className="px-2 py-0.5 rounded-full font-cta text-[9px] tracking-wider"
                            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: '#8B9AAB' }}>
                            +{cap.matchedFactors.length - 5} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-3 flex items-center gap-4">
                      <button onClick={() => setExpandedId(isExpanded ? null : cap.id)}
                        className="flex items-center gap-1.5 font-cta text-[10px] tracking-wider transition-colors"
                        style={{ color: isExpanded ? '#00D4FF' : '#8B9AAB' }}>
                        <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={11} />
                        {isExpanded ? 'COLLAPSE' : 'VIEW SPECS'}
                      </button>
                      <div className="w-px h-3 bg-white/10" />
                      <button onClick={() => toggleCompare(cap.id)}
                        disabled={!isCompared && compareIds.length >= 3}
                        className="flex items-center gap-1.5 font-cta text-[10px] tracking-wider transition-colors"
                        style={{
                          color: isCompared ? '#FF6B35' : compareIds.length >= 3 ? 'rgba(139,154,171,0.3)' : '#8B9AAB',
                          cursor: !isCompared && compareIds.length >= 3 ? 'not-allowed' : 'pointer',
                        }}>
                        <Icon name="ArrowsRightLeftIcon" size={11} />
                        {isCompared ? 'COMPARING' : 'ADD TO COMPARE'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded specs */}
                {isExpanded && (
                  <div className="mt-4 pt-4 grid grid-cols-1 md:grid-cols-2 gap-5"
                    style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div>
                      <p className="font-cta text-[10px] text-technical-gray/60 tracking-[0.2em] mb-3">TECHNICAL SPECIFICATIONS</p>
                      <div className="space-y-2">
                        {cap.specs.map((spec) => (
                          <div key={spec.label} className="flex justify-between items-center py-1.5 px-3 rounded-lg"
                            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <span className="font-body text-xs text-technical-gray">{spec.label}</span>
                            <span className="font-cta text-xs text-foreground">{spec.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-cta text-[10px] text-technical-gray/60 tracking-[0.2em] mb-3">KEY STRENGTHS</p>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {cap.strengths.map((s) => (
                          <span key={s} className="px-2.5 py-1 rounded-lg font-body text-xs text-foreground"
                            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            {s}
                          </span>
                        ))}
                      </div>
                      <p className="font-cta text-[10px] text-technical-gray/60 tracking-[0.2em] mb-3">CERTIFICATIONS</p>
                      <div className="flex flex-wrap gap-1.5">
                        {cap.certifications.map((c) => (
                          <span key={c} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-cta text-[10px] tracking-wider"
                            style={{ background: 'rgba(30,123,60,0.1)', border: '1px solid rgba(30,123,60,0.3)', color: '#1E7B3C' }}>
                            <Icon name="ShieldCheckIcon" size={10} />
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {noMatchCount > 0 && (
        <div className="text-center py-3">
          <span className="font-cta text-[10px] text-technical-gray/40 tracking-[0.2em]">
            {noMatchCount} CAPABILITIES NOT MATCHED TO CURRENT REQUIREMENTS
          </span>
        </div>
      )}
    </div>
  );
};

export default MatchResultsPanel;
